import { createAuthenticatedFetch } from './types/core/common';
import type {
  ExportFieldMapping,
  ExportFieldMappingCreate,
  ExportFieldMappingUpdate,
  ExportMapping,
  ExportMappingCreate,
  ExportMappingExport,
  ExportMappingImport,
  ExportMappingUpdate,
  ExportPreview,
  MetamodelResponse
} from './types/export/mappings';

const api = createAuthenticatedFetch();

/**
 * Service pour la gestion des mappings d'export
 */
export const exportMappingsService = {
  // ===============================
  // CRUD Export Mappings
  // ===============================

  /**
   * Récupère tous les mappings d'export de l'utilisateur
   */
  async getAll(skip = 0, limit = 100): Promise<ExportMapping[]> {
    const response = await api.get<ExportMapping[]>(`/export-mappings/?skip=${skip}&limit=${limit}`);
    return response;
  },

  /**
   * Récupère un mapping d'export spécifique
   */
  async getById(uuid: string): Promise<ExportMapping> {
    const response = await api.get<ExportMapping>(`/export-mappings/${uuid}`);
    return response;
  },

  /**
   * Crée un nouveau mapping d'export
   */
  async create(mappingData: ExportMappingCreate): Promise<ExportMapping> {
    const response = await api.post<ExportMapping>('/export-mappings/', mappingData);
    return response;
  },

  /**
   * Met à jour un mapping d'export
   */
  async update(uuid: string, mappingData: ExportMappingUpdate): Promise<ExportMapping> {
    const response = await api.put<ExportMapping>(`/export-mappings/${uuid}`, mappingData);
    return response;
  },

  /**
   * Supprime un mapping d'export
   */
  async delete(uuid: string): Promise<void> {
    await api.delete(`/export-mappings/${uuid}`);
  },

  // ===============================
  // Endpoints spécialisés
  // ===============================

  /**
   * Récupère les templates prédéfinis
   */
  async getTemplates(): Promise<ExportMapping[]> {
    const response = await api.get<ExportMapping[]>('/export-mappings/templates');
    return response;
  },

  /**
   * Duplique un mapping d'export
   */
  async duplicate(uuid: string): Promise<ExportMapping> {
    const response = await api.post<ExportMapping>(`/export-mappings/${uuid}/duplicate`);
    return response;
  },

  /**
   * Récupère le métamodèle universel (structure des champs exportables)
   * Le métamodèle est universel et ne dépend pas d'un mapping spécifique.
   * Il décrit la structure complète des données exportables (memo + sections dynamiques).
   */
  async getUniversalMetamodel(): Promise<MetamodelResponse> {
    return await api.get<MetamodelResponse>('/export-mappings/metamodel');
  },

  /**
   * Exporte la configuration d'un mapping
   */
  async exportConfig(uuid: string): Promise<ExportMappingExport> {
    const response = await api.get<ExportMappingExport>(`/export-mappings/${uuid}/export`);
    return response;
  },

  /**
   * Importe une configuration de mapping
   */
  async importConfig(importData: ExportMappingImport): Promise<ExportMapping> {
    const response = await api.post<ExportMapping>('/export-mappings/import', importData);
    return response;
  },

  /**
   * Génère un aperçu du résultat d'export
   */
  async preview(uuid: string): Promise<ExportPreview> {
    const response = await api.get<ExportPreview>(`/export-mappings/${uuid}/preview`);
    return response;
  },

  // ===============================
  // CRUD Field Mappings
  // ===============================

  /**
   * Récupère tous les field mappings d'un export mapping
   */
  async getFieldMappings(mappingUuid: string): Promise<ExportFieldMapping[]> {
    const response = await api.get<ExportFieldMapping[]>(`/export-mappings/${mappingUuid}/field-mappings`);
    return response;
  },

  /**
   * Crée un nouveau field mapping
   */
  async createFieldMapping(mappingUuid: string, fieldMappingData: ExportFieldMappingCreate): Promise<ExportFieldMapping> {
    const response = await api.post<ExportFieldMapping>(`/export-mappings/${mappingUuid}/field-mappings`, fieldMappingData);
    return response;
  },

  /**
   * Met à jour un field mapping
   */
  async updateFieldMapping(
    mappingUuid: string, 
    fieldMappingId: number, 
    fieldMappingData: ExportFieldMappingUpdate
  ): Promise<ExportFieldMapping> {
    const response = await api.put<ExportFieldMapping>(
      `/export-mappings/${mappingUuid}/field-mappings/${fieldMappingId}`, 
      fieldMappingData
    );
    return response;
  },

  /**
   * Supprime un field mapping
   */
  async deleteFieldMapping(mappingUuid: string, fieldMappingId: number): Promise<void> {
    await api.delete(`/export-mappings/${mappingUuid}/field-mappings/${fieldMappingId}`);
  },

  // ===============================
  // Utilitaires
  // ===============================

  /**
   * Télécharge la configuration d'un mapping en tant que fichier JSON
   */
  async downloadConfig(uuid: string, filename?: string): Promise<void> {
    const config = await this.exportConfig(uuid);
    // Nettoyage : retirer source_field pour les champs statiques
    if (config && config.mapping && Array.isArray(config.mapping.field_mappings)) {
      config.mapping.field_mappings = config.mapping.field_mappings.map((field: any) => {
        if (field.source_entity === 'static') {
          // On crée une copie et on supprime source_field
          const copy = { ...field };
          delete copy.source_field;
          return copy;
        }
        return field;
      });
    }
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    const finalFilename = filename || this.generateExportFilename(config.mapping);
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Valide les données d'un mapping avant création/mise à jour
   */
  validateMapping(mappingData: ExportMappingCreate | ExportMappingUpdate): string[] {
    const errors: string[] = [];

    if (!mappingData.name || mappingData.name.trim().length < 3) {
      errors.push('Le nom du mapping est obligatoire (3 caractères minimum).');
    }

    if ('format_type' in mappingData) {
      if (!mappingData.format_type) {
        errors.push('Le type de format d\'export est obligatoire.');
      } else if (!['json', 'csv', 'xml'].includes(mappingData.format_type)) {
        errors.push(`Le type de format '${mappingData.format_type}' n'est pas valide.`);
      }
    }

    if ('field_mappings' in mappingData && mappingData.field_mappings) {
      if (!Array.isArray(mappingData.field_mappings)) {
        errors.push('Les mappings de champs doivent être une liste.');
        return errors;
      }
      
      mappingData.field_mappings.forEach((field: ExportFieldMappingCreate, index: number) => {
        const prefix = `Champ #${index + 1}`;
        if (!field.source_entity || !field.destination_path) {
          errors.push(`${prefix}: L'entité source et le chemin de destination sont obligatoires.`);
        }
        
        // Vérification spécifique pour les champs non-statiques
        if (field.source_entity !== 'static' && !field.source_field) {
          errors.push(`${prefix}: Le champ source est obligatoire pour les entités non-statiques.`);
        }
        
        // Vérification spécifique pour les valeurs statiques
        if (field.source_entity === 'static' && (!field.static_value || field.static_value.trim() === '')) {
          errors.push(`${prefix}: Une valeur statique est requise pour l'entité source "static".`);
        }
        
        if (field.destination_path && field.destination_path.trim().length === 0) {
          errors.push(`${prefix}: Le chemin de destination ne peut pas être vide.`);
        }
      });
    }

    return errors;
  },

  /**
   * Valide et nettoie les données d'un mapping importé
   * Cette fonction est plus permissive que validateMapping pour permettre l'import de formats légèrement différents
   */
  validateAndCleanImport(importData: any): { 
    isValid: boolean; 
    errors: string[]; 
    warnings: string[]; 
    cleanedData: ExportMappingCreate | null;
  } {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Étape 1: Détection du format (direct ou encapsulé)
    let mappingData = importData;
    
    if (importData.export_version && importData.mapping) {
      mappingData = importData.mapping;
      warnings.push(`Format d'export détecté (version ${importData.export_version})`);
    }
    
    // Étape 2: Validation de base
    if (!mappingData.name) {
      errors.push('Le nom du mapping est manquant');
    }
    
    if (!mappingData.field_mappings) {
      errors.push('La liste des field_mappings est manquante');
    } else if (!Array.isArray(mappingData.field_mappings)) {
      errors.push('Le champ field_mappings doit être un tableau');
    } else if (mappingData.field_mappings.length === 0) {
      warnings.push('La liste des field_mappings est vide');
    }
    
    // Si erreurs critiques, on s'arrête là
    if (errors.length > 0) {
      return { 
        isValid: false, 
        errors, 
        warnings, 
        cleanedData: null 
      };
    }
    
    // Étape 3: Normalisation du format_type
    const format_type = mappingData.format_type || 'json';
    if (!['json', 'csv', 'xml'].includes(format_type)) {
      warnings.push(`Format type '${format_type}' non reconnu, utilisation de 'json' par défaut`);
    }
    
    // Étape 4: Filtrage et nettoyage des field_mappings
    const validFieldMappings = mappingData.field_mappings
      .filter((field: any) => {
        // Critères minimaux pour un champ valide
        const isValid = field && 
                       (field.source_entity || field.source_entity === 'static') && 
                       (field.source_entity === 'static' || field.source_field) && 
                       field.destination_path;
        
        if (!isValid) {
          warnings.push(`Champ ignoré car incomplet: ${JSON.stringify(field)}`);
        }
        
        return isValid;
      })
      .map((field: any, index: number) => {
        // Normaliser chaque champ
        const base = {
          source_entity: field.source_entity,
          destination_path: field.destination_path,
          field_label: field.field_label || field.label || '',
          field_description: field.field_description || field.description || '',
          static_value: field.static_value || '',
          transformation_rule: field.transformation_rule || null,
          ordre: field.ordre !== undefined ? field.ordre : index
        };
        if (field.source_entity !== 'static') {
          return {
            ...base,
            source_field: field.source_field || ''
          };
        } else {
          return base;
        }
      });
    
    // Vérification: au moins un champ valide après filtrage
    if (validFieldMappings.length === 0) {
      errors.push('Aucun champ valide trouvé dans le mapping');
      return { 
        isValid: false, 
        errors, 
        warnings, 
        cleanedData: null 
      };
    }
    
    // Étape 5: Normalisation des export_options
    const exportOptions = Array.isArray(mappingData.export_options) 
      ? mappingData.export_options.map((option: any) => ({
          option_key: option.option_key || '',
          option_value: option.option_value || '',
          option_type: option.option_type || 'string'
        }))
      : [];
    
    // Construction du mapping nettoyé
    const cleanedData: ExportMappingCreate = {
      name: `${mappingData.name} (Importé)`,
      description: mappingData.description || '',
      format_type: ['json', 'csv', 'xml'].includes(format_type) ? format_type : 'json',
      is_active: false, // Inactif par défaut pour la sécurité
      is_template: false,
      field_mappings: validFieldMappings,
      export_options: exportOptions
    };
    
    return {
      isValid: true,
      errors,
      warnings,
      cleanedData
    };
  },

  /**
   * Génère un nom de fichier d'export sécurisé
   */
  generateExportFilename(mapping: ExportMapping | ExportMappingCreate): string {
    const sanitizedName = (mapping.name || 'sans_nom').replace(/[^a-zA-Z0-9_.-]/g, '_');
    const dateString = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const format = mapping.format_type || 'json';
    return `export_${sanitizedName}_${dateString}.${format}`;
  }
}; 